import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Mail, FileText } from 'lucide-react';
import { Textarea } from '../../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Card, Pill, PrimaryButton } from '../ui';
import { adminApi, PROGRAM_NAMES } from '../../../pages/journey/journeyApi';

const TEXTS = [['current_situation', 'Current career situation'], ['strengths_text', 'Strengths'], ['development_text', 'Development areas'], ['explanation', 'Why this recommendation']];

const EmailPreview = ({ studentId }) => {
  const [mail, setMail] = useState(null);
  const open = () => adminApi(`/students/${studentId}/email-preview`).then(setMail).catch((e) => toast.error(e.message));
  return (<>
    <button onClick={open} className="inline-flex items-center gap-2 text-sm font-semibold text-teal-700" data-testid="rec-email-preview-btn"><Mail className="w-4 h-4" /> Preview email</button>
    <Dialog open={!!mail} onOpenChange={(o) => !o && setMail(null)}>
      <DialogContent className="max-w-3xl" data-testid="email-preview-dialog">
        <DialogHeader><DialogTitle className="font-outfit">{mail?.subject}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">To: {mail?.to} · <strong className="text-amber-700" data-testid="email-preview-status">{mail?.status}</strong></p>
        {mail && <iframe title="email" srcDoc={mail.html} className="w-full h-[60vh] rounded-xl border" data-testid="email-preview-frame" />}
      </DialogContent>
    </Dialog>
  </>);
};

const Engine = ({ e }) => (
  <div className="rounded-xl bg-slate-50 p-4 text-sm mb-4" data-testid="rec-engine-output">
    <p className="text-xs uppercase tracking-widest text-teal-700 mb-2">Recommendation rules output</p>
    <p><strong>Primary:</strong> {e.primary ? PROGRAM_NAMES[e.primary] : 'None needed'} · <strong>Level:</strong> {e.provisional_level}</p>
    <ul className="mt-2 space-y-1">{e.gaps.map((g) => <li key={g.area}>• <strong>{g.area}</strong> ({g.severity}): {g.reason}</li>)}</ul>
    <p className="mt-2 text-xs text-slate-500">Not recommended: {e.not_recommended.map((p) => PROGRAM_NAMES[p]).join(', ') || '—'}</p>
  </div>
);

export const RecommendationEditor = ({ student, onSaved }) => {
  const rec = student.recommendation;
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState(rec);
  const generate = async () => {
    setBusy(true);
    try { const r = await adminApi(`/students/${student.id}/recommendation/generate`, { method: 'POST' }); setF(r); toast.success('Draft summary generated'); onSaved(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const save = async (approve) => {
    try {
      const r = await adminApi(`/students/${student.id}/recommendation`, { method: 'PUT', body: { ...TEXTS.reduce((a, [k]) => ({ ...a, [k]: f[k] }), {}), primary: f.primary, options: f.options, approve, approved_by: 'Priya (Career Coach)' } });
      const note = { sending: ' · email is on its way to the student', awaiting_activation_invite: ' · send their activation invite from Onboarding to notify them' }[r?.email_status] || '';
      toast.success(approve ? `Approved and published to student dashboard${note}` : 'Draft saved', { duration: 7000 }); onSaved();
    } catch (e) { toast.error(e.message); }
  };
  const toggle = (p) => setF({ ...f, options: f.options.includes(p) ? f.options.filter((x) => x !== p) : [...f.options, p] });
  const completed = student.consultation.status === 'completed';
  return (
    <Card data-testid="recommendation-editor">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <p className="font-outfit font-semibold text-[#0b1f4d]">Personalized Career Summary</p>
        {rec && <Pill className={rec.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-50 text-amber-700'} data-testid="rec-status">{rec.status}</Pill>}
      </div>
      {!completed && <p className="text-sm text-slate-500" data-testid="rec-needs-consultation">Complete the consultation before generating a recommendation.</p>}
      {completed && <button onClick={generate} disabled={busy} className="inline-flex items-center gap-2 text-sm font-semibold text-[#0b1f4d] border border-slate-200 rounded-full px-4 py-2 mb-4" data-testid="rec-generate-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} {rec ? 'Rebuild draft from findings' : 'Build draft from findings'}</button>}
      {f && <>
        <Engine e={f.engine} />
        {TEXTS.map(([k, l]) => <label key={k} className="block text-sm mb-3">{l}<Textarea rows={3} value={f[k] || ''} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`rec-${k}-input`} /></label>)}
        <p className="text-sm mb-2">Programs offered (highlighted = recommended)</p>
        <div className="flex flex-wrap gap-2 mb-4">{['direction', 'skills', 'readiness', 'integrated'].map((p) => (
          <span key={p} className="inline-flex items-center gap-1 text-xs border rounded-full pl-2 pr-1 py-1">
            <input type="checkbox" checked={f.options.includes(p)} onChange={() => toggle(p)} data-testid={`rec-option-${p}`} /> {PROGRAM_NAMES[p]}
            <button onClick={() => setF({ ...f, primary: p, options: f.options.includes(p) ? f.options : [p, ...f.options] })} data-testid={`rec-primary-${p}`}
              className={`ml-1 px-2 py-0.5 rounded-full ${f.primary === p ? 'bg-teal-500 text-white' : 'bg-slate-100'}`}>★</button></span>))}</div>
        <div className="flex flex-wrap items-center gap-4">
          <button onClick={() => save(false)} className="text-sm font-semibold text-slate-600" data-testid="rec-save-btn">Save edits</button>
          <PrimaryButton onClick={() => save(true)} data-testid="rec-approve-btn">Approve & publish</PrimaryButton>
          <EmailPreview studentId={student.id} />
        </div>
      </>}
    </Card>
  );
};
